import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Input, Button, Card, Typography, Form, Alert, Space, message } from 'antd';
import { processPayment } from '../../services/api';

const { Title, Text } = Typography;

const UPIPayment = ({ 
  payeeAddress, 
  payeeName, 
  amount, 
  transactionNote,
  domainName,
  onPaymentCreated,  // For initial payment creation
  onPaymentComplete  // For when payment is verified
}) => {
  const [userUpiId, setUserUpiId] = useState('');
  const [isValidUpi, setIsValidUpi] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(''); // 'created', 'verifying', 'verified'
  const [form] = Form.useForm();

  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // UPI ID validation regex pattern
  const upiPattern = /^[\w\.\-_]{3,}@[\w]{3,}$/;

  const handleUpiValidation = (_, value) => {
    if (!value) {
      return Promise.reject('UPI ID is required');
    }
    if (!upiPattern.test(value)) {
      return Promise.reject('Invalid UPI ID format. Example: username@upi');
    }
    return Promise.resolve();
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      // Create payment first
      const paymentResult = await processPayment({
        amount: amount,
        domainName: domainName,
        paymentType: 'UPI',
        upiId: values.upiId
      });

      if (paymentResult.success) {
        setPaymentId(paymentResult.paymentId);
        setUserUpiId(values.upiId);
        setIsValidUpi(true);
        setPaymentStatus('created');
        onPaymentCreated(paymentResult.paymentId);
      } else {
        message.error('Failed to create payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      message.error('Failed to create payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = () => {
    const upiLink = `upi://pay?pa=${payeeAddress}&pn=${encodeURIComponent(payeeName)}&tn=${encodeURIComponent(transactionNote)}&am=${amount}`;
    window.location.href = upiLink;
  };

  const UpiInputSection = () => (
    <Form 
      form={form}
      onFinish={handleFormSubmit}
      layout="vertical"
    >
      <Form.Item
        name="upiId"
        label="Enter your UPI ID"
        rules={[
          { validator: handleUpiValidation }
        ]}
      >
        <Input 
          placeholder="username@upi"
          size="large"
        />
      </Form.Item>
      <Form.Item>
        <Button 
          type="primary" 
          htmlType="submit"
          block
          loading={loading}
        >
          Continue
        </Button>
      </Form.Item>
    </Form>
  );

  const PaymentSection = () => {
    if (!isValidUpi) return null;

    const upiLink = `upi://pay?pa=${payeeAddress}&pn=${encodeURIComponent(payeeName)}&tn=${encodeURIComponent(transactionNote)}&am=${amount}`;

    return (
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        {paymentStatus === 'verifying' ? (
          <Alert
            message="Verifying payment..."
            type="info"
            showIcon
          />
        ) : paymentStatus === 'verified' ? (
          <Alert
            message="Payment verified! Proceeding with registration..."
            type="success"
            showIcon
          />
        ) : (
          <>
            <Alert
              message={isMobileDevice ? "Click the button below to pay" : "Scan with any UPI app to pay"}
              type="info"
              showIcon
            />
            {isMobileDevice ? (
              <Button 
                type="primary"
                size="large"
                onClick={handlePayClick}
                block
              >
                Pay ₹{amount}
              </Button>
            ) : (
              <QRCodeSVG value={upiLink} size={256} />
            )}
            <Button 
              onClick={() => {
                setPaymentStatus('verifying');
                onPaymentComplete(paymentId);
              }}
              type="primary"
            >
              I have completed the payment
            </Button>
          </>
        )}
      </Space>
    );
  };

  return (
    <Card style={{ maxWi1dth: 400, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Space direction="vertical" size="small">
          <Text type="secondary">Paying to: {payeeName}</Text>
          <Text strong style={{ fontSize: 18 }}>Amount: ₹{amount}</Text>
            {transactionNote && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Note: {transactionNote}
              </Text>
            )}
          </Space>
        </div>

        {!isValidUpi ? (
          <UpiInputSection />
        ) : (
          <PaymentSection />
        )}
      </Space>
    </Card>
  );
};

export default UPIPayment;